import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { AlertTriangle, CheckCircle2, CreditCard } from "lucide-react";

export default function Billing() {
  const [statements, setStatements] = useState([]);
  const [rebills, setRebills] = useState([]);

  const load = async () => {
    const [s, r] = await Promise.all([api.get("/billing/statements"), api.get("/billing/rebills")]);
    setStatements(s.data.statements);
    setRebills(r.data);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    try { await api.post("/billing/rebills/generate-sample"); toast.success("Sample rebill generated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const charge = async (rb) => {
    try {
      await api.post(`/billing/rebills/${rb.rebill_id}/charge`);
      toast.success("Rebill charged from wallet"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Charge failed"); }
  };

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Billing</div>
        <h1 className="font-display text-3xl font-black tracking-tighter">Statements & Rebills</h1>
      </div>

      {/* Rebills section */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-xl font-bold">Surcharge rebills</div>
            <div className="text-sm text-zinc-600">Post-shipment adjustments reported by carriers. Charged against your wallet.</div>
          </div>
          <button data-testid="generate-rebill-btn" onClick={generate} className="border border-zinc-300 text-sm px-3 py-1.5 rounded-sm hover:bg-zinc-50">
            + Simulate rebill
          </button>
        </div>
        {rebills.length === 0 ? (
          <div className="py-8 text-center text-zinc-500 text-sm">No rebills yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="text-left py-2">Date</th>
                <th className="text-left">Shipment</th>
                <th className="text-left">Reason</th>
                <th className="text-left">Status</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rebills.map((r) => (
                <tr key={r.rebill_id} className="border-b border-zinc-100" data-testid={`rebill-${r.rebill_id}`}>
                  <td className="py-2 font-mono-plex text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="font-mono-plex">{r.book_number}</td>
                  <td>{r.description}</td>
                  <td>
                    {r.status === "pending" ? (
                      <span className="px-2 py-0.5 rounded-sm text-xs font-medium uppercase tracking-wider bg-amber-50 text-amber-700 flex items-center gap-1 w-fit">
                        <AlertTriangle size={12} /> Pending
                      </span>
                    ) : r.status === "charged" ? (
                      <span className="px-2 py-0.5 rounded-sm text-xs font-medium uppercase tracking-wider bg-emerald-50 text-emerald-700 flex items-center gap-1 w-fit">
                        <CheckCircle2 size={12} /> Charged
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-sm text-xs uppercase tracking-wider bg-red-50 text-red-700">{r.status}</span>
                    )}
                  </td>
                  <td className="text-right font-mono-plex">${r.amount.toFixed(2)}</td>
                  <td className="text-right">
                    {r.status === "pending" && (
                      <button data-testid={`charge-rebill-${r.rebill_id}`} onClick={() => charge(r)} className="brand-bg text-white text-xs px-3 py-1 rounded-sm hover:opacity-90 flex items-center gap-1 ml-auto">
                        <CreditCard size={12} /> Charge
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Statements */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="font-display text-xl font-bold mb-4">Monthly statements</div>
        {statements.length === 0 ? (
          <div className="py-8 text-center text-zinc-500 text-sm">No statements yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="text-left py-2">Period</th>
                <th className="text-right">Shipments</th>
                <th className="text-right">Total spend</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((s) => (
                <tr key={s.period} className="border-b border-zinc-100">
                  <td className="py-2 font-mono-plex">{s.period}</td>
                  <td className="text-right">{s.shipments}</td>
                  <td className="text-right font-mono-plex">${s.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}
